/**
 * 🔄 AUTO-SYNCED FILE - DO NOT EDIT DIRECTLY
 * 
 * This file is automatically synced from:
 * extensions/nudgesmart-themeapp-extension/assets/treeCodec.js
 * 
 * To make changes, edit the source file and run:
 * npm run sync:core
 * 
 * Last synced: 2026-08-16T13:56:10.295Z
 */

/**
 * treeCodec (storefront decoder)
 *
 * Campaign designs published to cdn.nudgesmart.com have their structural, style
 * and element-type keys renamed, so the raw file does not read as documentation
 * of how the product is built. This restores the plain shape.
 *
 * WHERE THIS IS USED — exactly one place.
 *
 * The app-proxy manifest is PLAIN JSON and is not encoded: it carries campaign
 * metadata, targeting rules, a payloadUrl per campaign, and (for social proof and
 * for campaigns not yet published) the element tree inline. Nothing on the
 * manifest path needs decoding, which is why the inline fetch in
 * campaigns-tracker.liquid — and its sessionStorage cache-hit branch, and the
 * fetchCampaigns() fallback — are all untouched.
 *
 * Only bytes fetched FROM THE CDN are encoded, and those are fetched in one
 * place: campaignManager.js, just before a campaign displays.
 *
 * ⚠️ SYNCED FILE — the tables below must stay identical to the ones in
 * api/helpers/treeCodec.js. `node scripts/verify-tree-codec.mjs` asserts this;
 * a silent divergence decodes every published design incorrectly.
 *
 * NOT a security boundary. These tables ship in the storefront bundle and can be
 * read by anyone willing to open it.
 */

(function () {
  'use strict';

  var CODEC_VERSION = 1;
  var CODE_PREFIX = '$';

  /** The 10 structural keys on an element node. */
  var STRUCT_KEYS = {
    uid: "$a",
    type: "$b",
    pageId: "$c",
    rowId: "$d",
    colId: "$e",
    desktop: "$f",
    mobile: "$g",
    data: "$h",
    order: "$i",
    subElements: "$j"
  };

  /** APPEND ONLY — index is the wire value. Reordering breaks published payloads. */
  var ELEMENT_TYPES = [
    "NsButton", "NsCol", "NsCountdown", "NsCoupon", "NsDivider", "NsFeedback",
    "NsFloatingImage", "NsImage", "NsInput", "NsLuckyWheel", "NsPage",
    "NsPickAPresent", "NsProduct", "NsRow", "NsScratchCard", "NsSocialProof",
    "NsSpacer", "NsSurvey", "NsText", "NsTextarea"
  ];

  /** APPEND ONLY, same reasoning. Unmapped keys pass through untouched. */
  var STYLE_KEYS = [
    "alignItems", "alignSelf", "backdropFilter", "background", "backgroundColor",
    "backgroundImage", "backgroundPosition", "backgroundSize", "border",
    "borderBottom", "borderColor", "borderLeft", "borderRadius", "borderRight",
    "borderStyle", "borderTop", "borderWidth", "bottom", "boxShadow", "boxSizing",
    "color", "contentAlignment", "cursor", "display", "flex", "flexDirection",
    "flexWrap", "focusBorder", "fontFamily", "fontFeatureSettings", "fontItalic",
    "fontSize", "fontStyle", "fontVariantNumeric", "fontWeight", "gap", "height",
    "hidden", "hoverBackground", "hoverBoxShadow", "hoverColor", "justifyContent",
    "left", "letterSpacing", "lineHeight", "luckyWheel", "margin", "marginLeft",
    "marginRight", "marginTop", "maxWidth", "minHeight", "minWidth", "objectFit",
    "objectPosition", "opacity", "order", "overflow", "padding", "paddingBottom",
    "placeholderColor", "position", "resize", "right", "shadow", "textAlign",
    "textDecoration", "textShadow", "textTransform", "textUnderlineOffset",
    "textWrap", "top", "transform", "transformOrigin", "transition",
    "verticalAlign", "whiteSpace", "width", "zIndex"
  ];

  /**
   * Viewport keys outside an element node. `$v`-prefixed so the three code
   * namespaces stay disjoint: struct $a-$j, style $s0-$s26, viewport $v0/$v1.
   * base36(15) is "f" and base36(16) is "g", so reusing the element-level
   * $f / $g here would collide with borderTop and borderWidth.
   */
  var VIEWPORT_KEYS = { desktop: "$v0", mobile: "$v1" };

  // -- derived lookups -------------------------------------------------------

  function invert(table) {
    var out = {};
    for (var key in table) {
      if (Object.prototype.hasOwnProperty.call(table, key)) out[table[key]] = key;
    }
    return out;
  }

  var STRUCT_DECODE = invert(STRUCT_KEYS);

  var TYPE_DECODE = {};
  for (var i = 0; i < ELEMENT_TYPES.length; i++) TYPE_DECODE[i] = ELEMENT_TYPES[i];

  var CONFIG_ENCODE = { desktop: VIEWPORT_KEYS.desktop, mobile: VIEWPORT_KEYS.mobile };
  for (var s = 0; s < STYLE_KEYS.length; s++) {
    CONFIG_ENCODE[STYLE_KEYS[s]] = CODE_PREFIX + 's' + s.toString(36);
  }
  var CONFIG_DECODE = invert(CONFIG_ENCODE);

  // -- decode ----------------------------------------------------------------

  /**
   * Deep key rename. Used for style objects, `data`, and templateConfig.
   * Values are never touched — only property names.
   */
  function remapDeep(node, table) {
    if (Array.isArray(node)) {
      var arr = [];
      for (var i = 0; i < node.length; i++) arr.push(remapDeep(node[i], table));
      return arr;
    }
    if (!node || typeof node !== 'object') return node;

    var out = {};
    for (var key in node) {
      if (!Object.prototype.hasOwnProperty.call(node, key)) continue;
      out[table[key] || key] = remapDeep(node[key], table);
    }
    return out;
  }

  /**
   * `subElements` is an OBJECT keyed by slot name (image, nameText, priceText,
   * oldPriceText, button) whose values are element-shaped — not an array.
   */
  function remapSlots(slots, fn) {
    if (!slots || typeof slots !== 'object' || Array.isArray(slots)) return slots;
    var out = {};
    for (var slot in slots) {
      if (Object.prototype.hasOwnProperty.call(slots, slot)) out[slot] = fn(slots[slot]);
    }
    return out;
  }

  function decodeData(data) {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return data;

    var out = {};
    for (var key in data) {
      if (!Object.prototype.hasOwnProperty.call(data, key)) continue;
      var value = data[key];

      if (key === STRUCT_KEYS.subElements) {
        out.subElements = remapSlots(value, decodeElement);
      } else if (key === 'tileTemplate' && value && Array.isArray(value.elements)) {
        // A nested element tree inside data, read at element-renderer.js:996.
        var tile = remapDeep(value, CONFIG_DECODE);
        var nested = [];
        for (var t = 0; t < value.elements.length; t++) nested.push(decodeElement(value.elements[t]));
        tile.elements = nested;
        out[key] = tile;
      } else {
        out[CONFIG_DECODE[key] || key] = remapDeep(value, CONFIG_DECODE);
      }
    }
    return out;
  }

  function decodeElement(element) {
    if (!element || typeof element !== 'object' || Array.isArray(element)) return element;

    var out = {};
    for (var code in element) {
      if (!Object.prototype.hasOwnProperty.call(element, code)) continue;
      var key = STRUCT_DECODE[code] || code;
      var value = element[code];

      if (key === 'type') {
        out[key] = (typeof value === 'number' && TYPE_DECODE[value] !== undefined)
          ? TYPE_DECODE[value]
          : value;
      } else if (key === 'desktop' || key === 'mobile') {
        out[key] = remapDeep(value, CONFIG_DECODE);
      } else if (key === 'subElements') {
        out[key] = remapSlots(value, decodeElement);
      } else if (key === 'data') {
        out[key] = decodeData(value);
      } else {
        out[key] = value;
      }
    }
    return out;
  }

  /**
   * Decode a payload fetched from the CDN.
   *
   * A payload with no `v` is treated as already-plain and returned as-is. That is
   * what the app-proxy inline fallback serves, so callers can pass anything
   * through this without checking first.
   *
   * @param {object} payload
   * @returns {{template: object, templateConfig: object, templateVersion: string|null}}
   */
  function decodePayload(payload) {
    if (!payload || typeof payload !== 'object') {
      return { template: { elements: [] }, templateConfig: {}, templateVersion: null };
    }

    if (payload.v === undefined) {
      return {
        template: payload.template || { elements: [] },
        templateConfig: payload.templateConfig || {},
        templateVersion: payload.templateVersion || null
      };
    }

    if (payload.v !== CODEC_VERSION) {
      throw new Error(
        'treeCodec: cannot decode payload version ' + payload.v +
        ' with codec version ' + CODEC_VERSION
      );
    }

    var source = (payload.template && Array.isArray(payload.template.elements))
      ? payload.template.elements
      : [];
    var elements = [];
    for (var i = 0; i < source.length; i++) elements.push(decodeElement(source[i]));

    var template = {};
    for (var k in payload.template) {
      if (Object.prototype.hasOwnProperty.call(payload.template, k)) template[k] = payload.template[k];
    }
    template.elements = elements;

    return {
      template: template,
      templateConfig: remapDeep(payload.templateConfig || {}, CONFIG_DECODE),
      templateVersion: payload.templateVersion || null
    };
  }

  if (typeof window !== 'undefined') {
    window.NudgesmartTreeCodec = {
      CODEC_VERSION: CODEC_VERSION,
      decodePayload: decodePayload
    };
  }
})();
